import { useEffect, useRef, useState } from "react";
import { apiUrl } from "../utils/jwt-storage";

/**
 * Detects whether the desktop proxy (or the public web app) can
 * actually reach upstream.
 *
 * Implementation:
 *   - Periodically GET ``/api/version/``. The desktop proxy answers
 *     200 when Django is reachable, and 503 with
 *     ``{"error": "upstream unreachable: ..."}`` when it isn't.
 *     On the plain web build the request just succeeds or fails
 *     against Django directly.
 *   - Listens to the browser ``online`` / ``offline`` events for
 *     instant feedback when connectivity flips.
 *
 * The two are combined: any signal that says "offline" is honoured
 * immediately; recovery requires a successful poll to avoid
 * flickering on flaky hotspots.
 */
/**
 * Synchronous read of the latest cloud-reachability state — same
 * underlying signal ``useOnlineStatus`` returns, but usable from
 * non-React code (event handlers, util functions). Returns true
 * when the cloud probe last succeeded; false when the latest probe
 * timed out, returned 503 upstream-unreachable, or threw.
 *
 * Defaults to true on first call (before any probe has completed)
 * so the optimistic-first-render path matches the hook.
 */
export const isCloudReachable = (): boolean => {
  try {
    if (typeof window === "undefined") return true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const flag = (window as any).__afri_cloud_up;
    return flag !== false;
  } catch (_) {
    return true;
  }
};

/**
 * Four-way connectivity, distinguishing what the single boolean above
 * cannot (they all rendered as "Offline"):
 *   "online"        cloud answered promptly
 *   "slow"          cloud answered, but slowly (> SLOW_MS) — degraded
 *                   link; nothing is broken, expect delays
 *   "server-down"   the device HAS a network but the cloud didn't
 *                   answer (5xx / timeout / refused) — our problem
 *   "no-internet"   the radio itself is off / no route (native plugin
 *                   or navigator.onLine says so) — their problem
 * Read via ``useConnectivity()``; the boolean hook is unchanged.
 */
export type Connectivity = "online" | "slow" | "server-down" | "no-internet";
const SLOW_MS = 2500;
let _connectivity: Connectivity = "online";
const _connListeners = new Set<(c: Connectivity) => void>();
const setConnectivity = (c: Connectivity) => {
  if (c === _connectivity) return;
  _connectivity = c;
  _connListeners.forEach((fn) => {
    try {
      fn(c);
    } catch (_) {
      /* ignore */
    }
  });
};
export const getConnectivity = (): Connectivity => _connectivity;
export const useConnectivity = (): Connectivity => {
  const [c, setC] = useState<Connectivity>(_connectivity);
  useEffect(() => {
    _connListeners.add(setC);
    return () => {
      _connListeners.delete(setC);
    };
  }, []);
  return c;
};
const radioIsUp = (): boolean => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    if (w.__afri_native_connected === false) return false;
  } catch (_) {
    /* ignore */
  }
  return typeof navigator === "undefined" ? true : navigator.onLine !== false;
};

/* ── ONE shared probe loop ─────────────────────────────────────────
   ``useOnlineStatus`` used to start its own 12s probe per HOOK
   INSTANCE — every project card, pill, and net indicator. A page with
   30 cards ran 30 loops: /api/version hammered continuously and
   ``afri:online-confirmed`` fired ~15×/second (measured). One
   module-level loop now probes; every hook instance subscribes. */
let _online = true;
const _onlineListeners = new Set<(v: boolean) => void>();
let _probeStarted = false;
let _probedOnce = false;
let _failures = 0;
let _probeTimer: ReturnType<typeof setTimeout> | null = null;

const setOnlineShared = (v: boolean) => {
  if (v === _online) return;
  _online = v;
  _onlineListeners.forEach((fn) => {
    try {
      fn(v);
    } catch (_) {
      /* ignore */
    }
  });
};

const desktopShell = (): boolean =>
  typeof window !== "undefined" &&
  (window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost") &&
  window.location.port === "17080";

const probe = async (): Promise<void> => {
  let nowOnline = false;
  try {
    if (desktopShell()) {
      const r = await fetch("/api/afri/auth/cloud_reachable/", {
        method: "GET",
        credentials: "same-origin",
        cache: "no-store",
      });
      const b = (await r.json().catch(() => null)) as { reachable?: boolean } | null;
      nowOnline = !!b?.reachable;
      setOnlineShared(nowOnline);
      _probedOnce = true;
      _failures = 0;
    } else {
      const t0 = performance.now();
      const r = await fetch(apiUrl("/api/version/"), {
        method: "GET",
        credentials: "same-origin",
        cache: "no-store",
      });
      const elapsed = performance.now() - t0;
      if (r.status === 503) {
        const text = await r
          .clone()
          .text()
          .catch(() => "");
        nowOnline = !/upstream unreachable/.test(text);
      } else {
        nowOnline = r.ok || r.status < 500;
      }
      setOnlineShared(nowOnline);
      _probedOnce = true;
      _failures = 0;
      setConnectivity(nowOnline ? (elapsed > SLOW_MS ? "slow" : "online") : "server-down");
    }
  } catch {
    _failures += 1;
    const radioDown = !radioIsUp();
    const decisive = _probedOnce || radioDown || _failures >= 2;
    if (decisive) {
      setOnlineShared(false);
      setConnectivity(radioDown ? "no-internet" : "server-down");
    }
  } finally {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__afri_cloud_up = nowOnline;
    } catch (_) {
      /* swallow */
    }
    if (nowOnline) {
      try {
        window.dispatchEvent(new CustomEvent("afri:online-confirmed"));
      } catch (_) {
        /* swallow */
      }
    } else {
      try {
        window.dispatchEvent(new CustomEvent("afri:cloud-down"));
      } catch (_) {
        /* swallow */
      }
    }
    if (_probeTimer) clearTimeout(_probeTimer);
    _probeTimer = setTimeout(probe, nowOnline ? 12000 : 3000);
  }
};

const startSharedProbe = (): void => {
  if (_probeStarted || typeof window === "undefined") return;
  _probeStarted = true;
  probe();

  window.addEventListener("online", () => {
    probe();
  });
  window.addEventListener("offline", () => {
    if (_probedOnce) setOnlineShared(false);
  });
  // Native Capacitor network plugin — authoritative and instant.
  window.addEventListener("afri:native-network", (e: Event) => {
    const detail = (e as CustomEvent<{ connected?: boolean }>).detail;
    if (detail?.connected === false) {
      setOnlineShared(false);
      setConnectivity("no-internet");
      try {
        (window as any).__afri_cloud_up = false;
      } catch (_) {
        /* ignore */
      }
    } else if (detail?.connected === true) {
      probe();
    }
  });
  // drainOutbox proof-of-reachability (fires once per drain).
  window.addEventListener("afri:online-confirmed", () => {
    setOnlineShared(true);
  });
};

export const useOnlineStatus = (): boolean => {
  const [online, setOnline] = useState(_online);
  useEffect(() => {
    startSharedProbe();
    _onlineListeners.add(setOnline);
    setOnline(_online);
    return () => {
      _onlineListeners.delete(setOnline);
    };
  }, []);
  return online;
};
